import { SiteCommissioningSummary } from "../../../../dtos/siteCommissioning";

export type IoSystem = "DMP" | "Paznic";
export type SensorSystem = IoSystem | "Other";
export type BiometricDeviceType = "Fingerprint" | "Palm";

export const HARDWARE_NOT_CONFIGURED = "Not Configured";
export const HARDWARE_NOT_AVAILABLE = "Not Available";
export const HARDWARE_NO_SENSORS_INSTALLED = "None installed";

export function ioSystemLabel(system: IoSystem): string {
    return `${system} I/O`;
}

export function sensorSystemLabel(system: SensorSystem): string {
    return `${system} sensors`;
}

export interface HardwareConfigurationSummary {
    ioSystems: IoSystem[];
    biometricDeviceType: BiometricDeviceType | null;
    sensorsInstalled: boolean;
    sensorSystems: SensorSystem[];
    hmiModel: string | null;
}

function ioSystemForDeviceType(deviceType: string): IoSystem {
    return deviceType === "dmp" ? "DMP" : "Paznic";
}

function sensorSystemFor(deviceType: string, sensorFields: Record<string, unknown>): SensorSystem {
    const ioSystem = ioSystemForDeviceType(deviceType);
    return ioSystem === "Paznic" && sensorFields.invertSerialNumber === true ? "Other" : ioSystem;
}

function ioDeviceKeyForSensorKey(sensorKey: string): string {
    const withoutPrefix = sensorKey.substring("sensor:".length);
    return withoutPrefix.substring(0, withoutPrefix.lastIndexOf(":"));
}

export type HardwareFieldValue =
    | { kind: "chips"; items: string[] }
    | { kind: "text"; text: string; isPlaceholder: boolean };

export interface HardwareField {
    key: "ioSystem" | "biometricDevice" | "sensors" | "hmiModel";
    label: string;
    value: HardwareFieldValue;
}

export function getHardwareFields(summary?: SiteCommissioningSummary): HardwareField[] {
    const hardware = deriveHardwareConfigurationSummary(summary);

    return [
        {
            key: "ioSystem",
            label: "I/O System",
            value: hardware.ioSystems.length > 0
                ? { kind: "chips", items: hardware.ioSystems.map(ioSystemLabel) }
                : { kind: "text", text: HARDWARE_NOT_CONFIGURED, isPlaceholder: true },
        },
        {
            key: "biometricDevice",
            label: "Biometric Device",
            value: {
                kind: "text",
                text: hardware.biometricDeviceType ?? HARDWARE_NOT_CONFIGURED,
                isPlaceholder: !hardware.biometricDeviceType,
            },
        },
        {
            key: "sensors",
            label: "Sensors",
            value: !hardware.sensorsInstalled
                ? { kind: "text", text: HARDWARE_NO_SENSORS_INSTALLED, isPlaceholder: true }
                : hardware.sensorSystems.length > 0
                    ? { kind: "chips", items: hardware.sensorSystems.map(sensorSystemLabel) }
                    : { kind: "text", text: "Unknown", isPlaceholder: true },
        },
        {
            key: "hmiModel",
            label: "HMI Model",
            value: {
                kind: "text",
                text: hardware.hmiModel ?? HARDWARE_NOT_AVAILABLE,
                isPlaceholder: !hardware.hmiModel,
            },
        },
    ];
}

export function buildHardwarePreview(summary?: SiteCommissioningSummary): string {
    const hardware = deriveHardwareConfigurationSummary(summary);
    const parts: string[] = [];

    if (hardware.ioSystems.length > 0) {
        parts.push(`${hardware.ioSystems.length} I/O system${hardware.ioSystems.length > 1 ? "s" : ""}`);
    }
    if (hardware.sensorsInstalled && hardware.sensorSystems.length > 0) {
        parts.push(`${hardware.sensorSystems.length} sensor system${hardware.sensorSystems.length > 1 ? "s" : ""}`);
    }
    if (hardware.biometricDeviceType) {
        parts.push(hardware.biometricDeviceType);
    }
    if (hardware.hmiModel) {
        parts.push(hardware.hmiModel);
    }

    return parts.length > 0 ? parts.join(" · ") : HARDWARE_NOT_CONFIGURED;
}

export function deriveHardwareConfigurationSummary(summary?: SiteCommissioningSummary): HardwareConfigurationSummary {
    const findSection = (name: string) => summary?.sections.find((s) => s.name === name);

    const ioDevicesSection = findSection("ioDevices");
    const ioDeviceItemKeys = Object.keys(ioDevicesSection?.items ?? {});
    const ioDeviceKeys = ioDeviceItemKeys.filter((key) => key.startsWith("ioDevice:"));
    const ioDeviceTypeByKey = new Map(ioDeviceKeys.map((key) => [key, key.split(":")[1]]));

    const ioSystems = Array.from(
        new Set(ioDeviceKeys.map((key) => ioSystemForDeviceType(ioDeviceTypeByKey.get(key) as string)))
    );

    const sensorKeys = ioDeviceItemKeys.filter((key) => key.startsWith("sensor:"));
    const sensorSystems = Array.from(
        new Set(
            Object.entries(ioDevicesSection?.items ?? {})
                .filter(([key]) => key.startsWith("sensor:"))
                .flatMap(([key, item]) => {
                    const deviceType = ioDeviceTypeByKey.get(ioDeviceKeyForSensorKey(key));
                    return deviceType === undefined ? [] : [sensorSystemFor(deviceType, item.fields)];
                })
        )
    );

    const biometricSection = findSection("biometricDevices");
    const biometricDeviceTypes = Object.values(biometricSection?.items ?? {}).map((item) => item.fields.type);
    const fingerprintConfigFields = findSection("fingerprintConfiguration")?.items?.config?.fields ?? {};
    const biometricDeviceType: BiometricDeviceType | null =
        biometricDeviceTypes.find((type): type is BiometricDeviceType => type === "Fingerprint" || type === "Palm") ??
        (Object.keys(fingerprintConfigFields).length > 0 ? "Fingerprint" : null);

    const hmiFields = findSection("hmiConfiguration")?.items?.config?.fields ?? {};
    const hmiModel = typeof hmiFields.model === "string" && hmiFields.model.trim().length > 0 ? hmiFields.model : null;

    return {
        ioSystems,
        biometricDeviceType,
        sensorsInstalled: sensorKeys.length > 0,
        sensorSystems,
        hmiModel,
    };
}
